import type { Area, Issue } from './types';
import { rollUpEpic, type EpicHealth } from './epic-rollup';

/**
 * THE WORKSPACE AREA ROLL-UP — every product area across every board the
 * Portfolio loaded, rolled up from the cards that sit in it. The sibling of the
 * by-epic workspace roll-up (src/epic-rollup.ts): an epic is "how is this one
 * initiative moving", an area is "where is the demand, and who carries it".
 *
 * KEYED BY NAME, not by id — deliberately, and unlike within a single board. A
 * card carries an area ID (`c.area`) that is local to its own board: board A's
 * "a1" is not board B's "a1", and the same area "Payments" may be a different id
 * on each board. So this resolves every card's id to its area NAME through that
 * board's own area list (handed in as `areasByBoard`, the same way the by-epic
 * lens is handed each board's cards), and groups by the name. An area that only
 * one board defines is still one row; the same name on three boards is one row
 * rolled up across all three. A card whose area id resolves to nothing on its
 * board — or that carries no area — falls into the honest "no area" bucket.
 *
 * ALL DERIVED. Lead (the plurality owner), health, progress and the open/total
 * counts are read straight from the member cards by rollUpEpic — the SAME
 * derivation the epics use — so an area and an epic never compute "at risk" or
 * "62%" two different ways. Nothing here is typed or stored.
 */

const isResolved = (s: Issue['status']): boolean => s === 'done' || s === 'wontdo';

/** One board an area has cards on, for the "On boards" cell. */
export interface AreaBoard {
  id: string;
  name: string;
  cards: number;
}

export interface WorkspaceArea {
  name: string;
  /** The area's colour, taken from the first board that defines the name — the
   *  same value the board card rail and the backlog chip use. '' if none set. */
  colour: string;
  /** The area's lead: the STORED lead set on the area if there is one, otherwise
   *  the derived plurality owner. '' if neither. See `leadStored`. */
  lead: string;
  /** True when `lead` is the stored area lead; false when it fell back to the
   *  derived plurality owner (shown as "auto"). */
  leadStored: boolean;
  /** True when a lead is STORED but holds no open card in the area — the named
   *  accountable person has drifted off the work; worth a quiet flag. */
  leadStale: boolean;
  health: EpicHealth;
  healthReason: string;
  progressPct: number;
  open: number;
  total: number;
  /** The boards this area has cards on, most cards first then by name. */
  boards: AreaBoard[];
}

/** The board name map the roll-up needs — just id → name (areas have no
 *  lifecycle tier, so no tier is needed, unlike the by-epic BoardTier). */
export interface AreaBoardMeta {
  id: string;
  name: string;
}

export interface WorkspaceAreas {
  /** One roll-up per distinct area NAME across the workspace, most open work first. */
  areas: WorkspaceArea[];
  /** Every card with no area (or an id its board does not define), across all boards. */
  noArea: { total: number; open: number };
  /** Every card considered, across all boards. */
  totalCards: number;
}

/**
 * Roll every area in the workspace up from the cards on every board.
 *
 * `areasByBoard` maps a board id to that board's area list, so a card's local
 * `area` id can be resolved to a name (and colour) within its own board. `boards`
 * names each board for the "On boards" cell. A card on a board the caller listed
 * no areas for keeps its work in the roll-up but cannot have its id resolved, so
 * it reads as "no area" — honest, not dropped.
 */
export function rollUpWorkspaceAreas(
  cardsByBoard: Map<string, Issue[]>,
  areasByBoard: Map<string, Area[]>,
  boards: AreaBoardMeta[],
  now = Date.now(),
): WorkspaceAreas {
  const meta = new Map(boards.map((b) => [b.id, b]));
  const byName = new Map<string, Issue[]>();
  const colourByName = new Map<string, string>();
  // The stored lead per area NAME — taken from the first board that sets one, the
  // same way the colour is, so the cross-board row has a single accountable lead.
  const leadByName = new Map<string, string>();
  // area name → board id → how many of the area's cards sit on that board
  const boardsByArea = new Map<string, Map<string, number>>();
  let noAreaTotal = 0;
  let noAreaOpen = 0;
  let totalCards = 0;

  for (const [boardId, cards] of cardsByBoard) {
    const areaById = new Map((areasByBoard.get(boardId) ?? []).map((a) => [a.id, a]));
    for (const c of cards) {
      totalCards++;
      const area = c.area ? areaById.get(c.area) : undefined;
      if (!area) {
        noAreaTotal++;
        if (!isResolved(c.status)) noAreaOpen++;
        continue;
      }
      const name = area.name;
      const list = byName.get(name);
      if (list) list.push(c);
      else byName.set(name, [c]);
      if (!colourByName.has(name) && area.colour) colourByName.set(name, area.colour);
      if (!leadByName.has(name) && area.lead && area.lead.trim()) leadByName.set(name, area.lead.trim());
      let bm = boardsByArea.get(name);
      if (!bm) boardsByArea.set(name, (bm = new Map()));
      bm.set(boardId, (bm.get(boardId) ?? 0) + 1);
    }
  }

  const areas: WorkspaceArea[] = [...byName.keys()].map((name) => {
    const cards = byName.get(name)!;
    const roll = rollUpEpic(name, cards, [], now);
    const counts = boardsByArea.get(name)!;
    const areaBoards: AreaBoard[] = [...counts]
      .map(([id, n]) => ({ id, name: meta.get(id)?.name ?? '—', cards: n }))
      .sort((a, b) => b.cards - a.cards || a.name.localeCompare(b.name));
    const storedLead = leadByName.get(name) ?? '';
    const leadStale = storedLead !== '' && !cards.some((c) => c.assignee === storedLead && !isResolved(c.status));
    return {
      name,
      colour: colourByName.get(name) ?? '',
      lead: storedLead || roll.owner,
      leadStored: storedLead !== '',
      leadStale,
      health: roll.health,
      healthReason: roll.healthReason,
      progressPct: roll.progressPct,
      open: roll.openCount,
      total: roll.totalCount,
      boards: areaBoards,
    };
  });
  // Most open demand first, then name — a stable order, where the attention goes.
  areas.sort((a, b) => b.open - a.open || a.name.localeCompare(b.name));

  return { areas, noArea: { total: noAreaTotal, open: noAreaOpen }, totalCards };
}
